import type { useNavigate } from '@change-corgi/core/react/router';
import type { TrackingFn } from '@change-corgi/core/react/tracking';

import type { useFlyerDownloadModalContext } from 'src/app/pages/starterDashboardV2/shared/components';
import { shareNative } from 'src/app/shared/share/mobileNativeShare';

import type { Task } from '../../../../types';

import {
	navigateToGroupRecommendations,
	navigateToPetitionActivity,
	navigateToPostUpdate,
	navigateToProfile,
	navigateToReadyToSharePosts,
} from './taskActionNavigate';

export type GetActionParams = Readonly<{
	id: Task['id'];
	name: Task['name'];
	status?: Task['status'];
	version?: 'new_12_2025' | 'new_ai_03_2026';
}>;

export type TaskActionContext = {
	petition: { slug: string };
	navigate: ReturnType<typeof useNavigate>;
	track: TrackingFn;
	isMobile: boolean;
	shareUrlWithUtmParams: string;
	refreshData: () => void;
	completeTask: (id: string, name: string) => Promise<unknown>;
	triggerSharePetitionModal: () => Promise<void>;
	openFlyerDownloadModal: ReturnType<typeof useFlyerDownloadModalContext>['openModal'];
	showMultiDownloadFlyersTask: boolean;
	data: { canEditUrl: boolean };
	actions: { onEditUrl: () => Promise<void> };
};

type TaskHandler = (params: GetActionParams, context: TaskActionContext) => () => Promise<boolean>;

const handleCompleteProfile: TaskHandler = ({ version }, { petition, navigate, track }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', {
			task: 'profile',
			version,
		});
		navigateToProfile(petition.slug, navigate);
		return true;
	};
};

const handleCustomizeLink: TaskHandler = ({ name, version }, { track, data, actions, refreshData }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', { task: name, version });
		if (!data.canEditUrl) {
			return true;
		}
		await actions.onEditUrl();
		refreshData();
		return true;
	};
};

const handleShareFriends: TaskHandler = (
	{ id, name, status = 'ACTIVE', version },
	{ track, isMobile, shareUrlWithUtmParams, triggerSharePetitionModal, completeTask, refreshData },
) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', { task: name, version });
		if (isMobile) {
			await shareNative(
				{ url: shareUrlWithUtmParams },
				{
					trackingEvents: {
						success: 'share_friends_success',
						failure: 'share_friends_failure',
					},
					trackingData: { task: name },
				},
				track,
			);
			if (status !== 'COMPLETED') {
				await completeTask(id, name);
				refreshData();
			}
		} else {
			await triggerSharePetitionModal();
		}
		return true;
	};
};

const handleShareCommunities: TaskHandler = ({ name, version }, { petition, navigate, track }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', { task: name, version });
		navigateToGroupRecommendations(petition.slug, navigate);
		return true;
	};
};

const handleShareFacebook: TaskHandler = ({ version }, { petition, navigate, track }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', {
			task: 'share_social',
			platform: 'facebook',
			version,
		});
		navigateToReadyToSharePosts(petition.slug, navigate, 'facebook');
		return true;
	};
};

const handleShareSocial: TaskHandler = ({ name, version }, { petition, navigate, track }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', { task: name, version });
		navigateToReadyToSharePosts(petition.slug, navigate);
		return true;
	};
};

const handleShareInstagram: TaskHandler = ({ version }, { petition, navigate, track }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', {
			task: 'share_social',
			platform: 'instagram',
			version,
		});
		navigateToReadyToSharePosts(petition.slug, navigate, 'instagram');
		return true;
	};
};

const handleDownloadFlyer: TaskHandler = (
	{ id, name, status = 'ACTIVE', version },
	{ track, showMultiDownloadFlyersTask, openFlyerDownloadModal, completeTask, refreshData },
) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', { task: name, version });
		if (showMultiDownloadFlyersTask) {
			openFlyerDownloadModal('starter_dashboard_task');
			refreshData();
			return true;
		}
		if (status !== 'COMPLETED') {
			await completeTask(id, name);
			refreshData();
		}
		return true;
	};
};

const handleDownloadSignatures: TaskHandler = ({ name, version }, { petition, navigate, track }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', { task: name, version });
		navigateToPetitionActivity(petition.slug, navigate);
		return true;
	};
};

const handleThankSupporters: TaskHandler = ({ name, version }, { petition, navigate, track }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', { task: name, version });
		navigateToPostUpdate(petition.slug, navigate);
		return true;
	};
};

const handlePromoteBasic: TaskHandler = ({ version }, { petition, navigate, track, refreshData }) => {
	return async () => {
		void track('petition_manage_tasks_cta_click', {
			task: 'promote_basic',
			version,
		});
		navigate(`/p/${petition.slug}/promote`);
		refreshData();
		return true;
	};
};

const taskHandlers: Readonly<Record<string, TaskHandler>> = {
	complete_profile: handleCompleteProfile,
	customize_link: handleCustomizeLink,
	share_friends: handleShareFriends,
	share_communities: handleShareCommunities,
	share_social: handleShareSocial,
	share_facebook: handleShareFacebook,
	share_instagram: handleShareInstagram,
	download_flyer: handleDownloadFlyer,
	download_signatures: handleDownloadSignatures,
	thank_supporters: handleThankSupporters,
	promote_basic: handlePromoteBasic,
};

export function createTaskAction(params: GetActionParams, context: TaskActionContext): () => Promise<boolean> {
	const handler = taskHandlers[params.name];
	return handler ? handler(params, context) : async () => true;
}
